/** @jsx jsx */

import _ from 'lodash';
import { Component, Fragment } from 'react';

import { jsx } from '@emotion/core';
import { LegacyButton, Margin } from '@zapier/design-system';

import CtaCreateZap from 'app/developer-v3/components/CtaCreateZap';
import DismissableHelpBox from 'app/developer-v3/components/DismissableHelpBox';
import EditorBodyLayout from 'app/developer-v3/layouts/EditorBodyLayout';
import HelpBoxApiConfig from 'app/developer-v3/components/HelpBoxApiConfig';
import NonFormaticFieldWrapper from 'app/developer-v3/components/NonFormaticFieldWrapper';
import OutputFieldsEditor from 'app/developer-v3/components/OutputFieldsEditor';
import RequestTester, {
  TestData,
} from 'app/developer-v3/components/RequestTester';
import SampleOutputEditor from 'app/developer-v3/components/SampleOutputEditor';
import SaveButtonGroup from 'app/developer-v3/components/SaveButtonGroup';
import SetupStep from 'app/developer-v3/components/SetupStep';
import Link from 'app/common/components/Link';
import {
  BASE_DOC_URL_VB,
  highlightTriggerApiKeys,
} from 'app/developer-v3/constants';
import {
  createValidator,
  deriveOutputFields,
  getHighlightTriggerApiFieldKey,
  getQueryHighlightStep,
  validateRequired,
} from 'app/developer-v3/utils';

import type { ErrorList } from 'app/developer-v3/types/validator';
import type {
  ApiHighlightField,
  FormFieldTemplate,
  OperationMethodType,
  QueryHighlight,
  TestLogsType,
  TestRequestType,
  TriggerParams,
} from 'app/developer-v3/types';
import type { AuthenticationType } from 'app/developer-v3/platformSchema/authentication';
import type {
  AuthFieldSchema,
  InputFieldSchema,
  OutputFieldSchema,
} from 'app/developer-v3/platformSchema/field';
import type {
  LegacyTriggerOperationSchema,
  LegacyTriggerSchema,
} from 'app/developer-v3/platformSchema/legacy';
import type { RequestSchema } from 'app/developer-v3/platformSchema/request';
import type {
  TriggerOperationSchema,
  TriggerOperationType,
  TriggerSchema,
} from 'app/developer-v3/platformSchema/trigger';

import Config from './Config';
import HookTester from './HookTester';
import NeedsAuthMessage from './NeedsAuthMessage';
import formFields from './formFields';
import { defaultOperation } from './constants';
import { getValidPerformObjectForOperationType } from './utils';

type Props = {
  appName: string;
  authFields: Array<AuthFieldSchema>;
  authType: AuthenticationType;
  executeRequest: (
    request: RequestSchema,
    authenticationId: string,
    operationMethod: OperationMethodType,
    testData: TestData
  ) => Promise<any>;
  getSuggestions: (operation: string) => Array<string>;
  isAuthSetupComplete: boolean;
  isReadOnly: boolean;
  isSaving: boolean;
  isTriggerSetup: boolean;
  params: TriggerParams;
  query: QueryHighlight;
  requestTemplate: RequestSchema;
  saveTriggerOperation: (
    operation: TriggerOperationSchema,
    operationLegacy?: LegacyTriggerOperationSchema
  ) => void;
  selectedApi: string;
  setUnsavedChanges: (hasUnsavedChanges: boolean) => void;
  testLogs: TestLogsType;
  testRequest: TestRequestType;
  trigger: TriggerSchema;
  triggerLegacy?: LegacyTriggerSchema;
  warnAboutTypeChange: () => void;
};

type State = {
  activeStep: number;
  errors: ErrorList;
  hasAttemptedSave: boolean;
  highlightField: ApiHighlightField;
  isFormValid: boolean;
  isSampleValid: boolean;
  outputFieldsEditorKey: number;
  sampleText: string;
  shouldWarnAboutTypeChange: boolean;
  trigger: TriggerSchema;
  triggerLegacy?: LegacyTriggerSchema;
};

class TriggerApi extends Component<Props, State> {
  validateForm: (values: { perform: string }) => ErrorList;

  getQueryStep(query: QueryHighlight = { highlight: '' }) {
    const { highlight } = query;

    const queryToStepsMap = {
      perform: 1,
      performGet: 1,
      performList: 1,
      performSubscribe: 1,
      performUnsubscribe: 1,
      canPaginate: 1,
      test: 2,
      sample: 3,
      outputFields: 3,
    };

    return getQueryHighlightStep(queryToStepsMap, highlight, 1);
  }

  getFieldToHighlight(
    query: QueryHighlight = { highlight: '', type: '', message: '' }
  ) {
    const { highlight = '', type = '', message = '' } = query;

    return {
      field: getHighlightTriggerApiFieldKey(highlight),
      type,
      message,
    };
  }

  state = {
    activeStep: this.getQueryStep(this.props.query),
    highlightField: this.getFieldToHighlight(this.props.query),
    errors: {},
    hasAttemptedSave: false,
    isSampleValid: true,
    isFormValid: !!this.props.trigger?.operation,
    outputFieldsEditorKey: 0,
    sampleText: JSON.stringify(
      this.props.trigger?.operation?.sample ?? {},
      null,
      2
    ),
    shouldWarnAboutTypeChange: true,
    trigger: {
      ...this.props.trigger,
      operation: {
        ...this.props.trigger?.operation,
        perform: getValidPerformObjectForOperationType(
          this.props.trigger?.operation?.type ?? 'polling',
          this.props.trigger?.operation?.perform
        ),
      },
    },
    ...(this.props.triggerLegacy
      ? { triggerLegacy: this.props.triggerLegacy }
      : {}),
  };

  componentDidUpdate(prevProps: Props) {
    this.refreshValidator(
      formFields[this.state.trigger.operation?.type ?? 'polling']
    );

    if (
      this.props.query &&
      this.props.query.highlight !== prevProps.query.highlight
    ) {
      this.setState({
        highlightField: this.getFieldToHighlight(this.props.query),
        activeStep: this.getQueryStep(this.props.query),
      });
    }
  }

  get isSaveDisabled() {
    return this.props.isReadOnly || !this.state.isSampleValid
      ? true
      : this.state.hasAttemptedSave
      ? !this.state.isFormValid
      : false;
  }

  get activeErrors() {
    return this.state.hasAttemptedSave ? this.state.errors : {};
  }

  get configHelpText() {
    return (
      this.state.triggerLegacy && {
        heading: 'Legacy Web Builder API Request',
        description: <HelpBoxApiConfig params={this.props.params} />,
      }
    );
  }

  get configFormValues() {
    const { trigger } = this.state;
    const { operation } = trigger;
    const { type = 'polling' } = operation;
    const isPolling = type === 'polling';
    const isHook = type === 'hook';

    if (!isPolling && !isHook) {
      throw Error(`Invalid trigger type: ${type}`);
    }

    const values = isPolling
      ? {
          canPaginate: operation?.canPaginate,
          perform: operation?.perform,
          type,
        }
      : {
          canPaginate: operation?.canPaginate,
          perform: operation?.perform,
          performList:
            operation?.performList ||
            (defaultOperation.hook?.performList ?? {}),
          performSubscribe:
            operation?.performSubscribe ||
            (defaultOperation.hook?.performSubscribe ?? {}),
          performUnsubscribe:
            operation?.performUnsubscribe ||
            (defaultOperation.hook?.performUnsubscribe ?? {}),
          type,
        };

    const performKeys = ['perform'];
    if (isHook) {
      performKeys.push(
        ...['performList', 'performSubscribe', 'performUnsubscribe']
      );
    }
    return values;
  }

  get configFormValuesLegacy() {
    return this.state.triggerLegacy ? this.state.triggerLegacy.operation : {};
  }

  get requestsForTesting() {
    return [
      {
        label: 'Perform',
        operationMethod: 'poll',
        request: this.configFormValues.perform,
      },
    ];
  }

  refreshValidator = (fields: Array<FormFieldTemplate>) => {
    this.validateForm = createValidator(fields, [validateRequired]);
  };

  setUnsavedChanges = () => this.props.setUnsavedChanges(true);
  unsetUnsavedChanges = () => this.props.setUnsavedChanges(false);

  validatePerform = (operation: TriggerOperationSchema) => {
    const { url, source } = operation.perform;
    const errors = this.validateForm
      ? this.validateForm({ perform: url || source })
      : {};
    const isFormValid = _.isEmpty(errors);

    this.setState({ isFormValid, errors });

    return isFormValid;
  };

  saveTrigger = (
    operation: TriggerOperationSchema,
    operationLegacy?: LegacyTriggerOperationSchema
  ) => {
    this.props.saveTriggerOperation(operation, operationLegacy);
    this.unsetUnsavedChanges();
  };

  onChangeOperationType = (typeNext: TriggerOperationType) => {
    const { trigger: initialTrigger, warnAboutTypeChange } = this.props;
    const { shouldWarnAboutTypeChange, trigger } = this.state;

    if (shouldWarnAboutTypeChange) {
      this.setState({ shouldWarnAboutTypeChange: false }, warnAboutTypeChange);
      return;
    }

    const performNext = getValidPerformObjectForOperationType(
      typeNext,
      initialTrigger?.operation?.perform
    );
    const triggerNext = {
      ...trigger,
      operation: {
        ...trigger?.operation,
        type: typeNext,
        perform: performNext,
      },
    };
    const isFormValid = this.validatePerform(triggerNext.operation);

    this.setState(
      {
        trigger: triggerNext,
        isFormValid,
        shouldWarnAboutTypeChange: true,
        errors: {},
      },
      this.setUnsavedChanges
    );
  };

  onChangeOperationLegacy = (value?: { url?: string; source?: string }) => {
    const { trigger, triggerLegacy } = this.state;
    const isLegacyTriggerHook = !!triggerLegacy?.operation?.hookType;

    const triggerLegacyNext = { ...triggerLegacy, operation: value };

    if (isLegacyTriggerHook) {
      const triggerNext = {
        ...trigger,
        operation: {
          ...trigger.operation,
          performList: {
            ...trigger.operation?.performList,
            source: value?.url
              ? `return z.legacyScripting.run(bundle, 'trigger', '${trigger.key}');`
              : undefined,
          },
        },
      };
      this.validatePerform(triggerNext.operation);
      this.setState(
        { trigger: triggerNext, triggerLegacy: triggerLegacyNext },
        this.setUnsavedChanges
      );
    } else {
      this.setState(
        { triggerLegacy: triggerLegacyNext },
        this.setUnsavedChanges
      );
    }
  };

  onChangeOperation = (key: string, value?: {} | Array<InputFieldSchema>) => {
    const { trigger } = this.state;
    const triggerNext = {
      ...trigger,
      operation: {
        ...trigger.operation,
        [key]: value,
      },
    };

    if (!_.isEqual(triggerNext, trigger)) {
      this.validatePerform(triggerNext.operation);
      this.setState({ trigger: triggerNext }, this.setUnsavedChanges);
    }
  };

  onUpdateSample = (
    value: string,
    _name: string,
    didConfirm: boolean,
    isValid?: boolean
  ) => {
    if (didConfirm) {
      this.setState({ isSampleValid: isValid });
      try {
        const jsonValue = JSON.parse(value);
        this.onChangeOperation(
          'sample',
          _.isEmpty(jsonValue) ? undefined : jsonValue
        );
      } catch (e) {
        // Ignore as people might be mid-typing
      }
    }

    this.setState({ sampleText: value }, this.setUnsavedChanges);
  };

  onGenerateOutputClick = () => {
    const { outputFieldsEditorKey, trigger } = this.state;
    const { outputFields, sample } = trigger.operation;
    this.onChangeOperation(
      'outputFields',
      deriveOutputFields({ outputFields, sample })
    );
    this.setState({
      outputFieldsEditorKey: outputFieldsEditorKey + 1,
    });
  };

  onUpdateOutputFields = (outputFields: Array<OutputFieldSchema>) => {
    this.onChangeOperation('outputFields', outputFields);
  };

  onIncrementActiveStep = () => {
    this.setState(prevState => ({
      activeStep: prevState.activeStep + 1,
    }));
  };

  onSaveAndIncrementActiveStep = () => {
    const { trigger, triggerLegacy } = this.state;
    if (this.validatePerform(trigger.operation)) {
      this.saveTrigger(
        trigger.operation,
        ...(triggerLegacy ? [triggerLegacy.operation] : [])
      );

      this.onIncrementActiveStep();
    }

    this.setState({ hasAttemptedSave: true });
  };

  render() {
    const {
      getSuggestions,
      isAuthSetupComplete,
      isReadOnly,
      isSaving,
      isTriggerSetup,
      params,
      testRequest,
      trigger: initialTrigger,
    } = this.props;
    const {
      activeStep,
      highlightField,
      outputFieldsEditorKey,
      sampleText,
      trigger,
      triggerLegacy,
    } = this.state;

    const isHook = trigger.operation?.type === 'hook';

    return (
      <EditorBodyLayout isDisabled={this.isSaveDisabled} isSaving={isSaving}>
        <Margin margin="0px 0px 40px 0px">
          {isTriggerSetup ? (
            <CtaCreateZap type="trigger" />
          ) : (
            <DismissableHelpBox
              description={
                <p>
                  Now, configure how to get data from your API endpoint, and
                  optionally send data from input fields. Select your trigger
                  type, fill in each field or customize the code, then test your
                  trigger and define the data it returns.{' '}
                  <Link
                    href={`${BASE_DOC_URL_VB}/build/trigger#3-enter-api-configuration`}
                    target="_blank"
                  >
                    Learn More
                  </Link>
                  .
                </p>
              }
              heading="Connect Your API"
              persistId="developer-trigger-api-endpoints"
            />
          )}
        </Margin>
        <SetupStep
          helpText={this.configHelpText}
          isInitiallyOpen={activeStep === 1}
          stepLabel="Step 1"
          title="Configure your API Request"
        >
          {triggerLegacy ? (
            <Config.Legacy
              hookType={triggerLegacy.operation.hookType}
              isDisabled={isReadOnly}
              onChange={this.onChangeOperationLegacy}
              value={this.configFormValuesLegacy}
            />
          ) : (
            <Config.Primary
              isDisabled={isReadOnly}
              meta={{
                getSuggestions,
                errors: this.activeErrors,
                info: highlightField,
              }}
              onChange={this.onChangeOperation}
              onChangeOperationType={this.onChangeOperationType}
              operationType={trigger.operation?.type ?? 'polling'}
              value={this.configFormValues}
            />
          )}
          <Margin margin="5px 0px 5px 0px">
            <SaveButtonGroup
              isDisabled={this.isSaveDisabled}
              isSaving={isSaving}
              onSave={this.onSaveAndIncrementActiveStep}
              saveText="Save API Request &amp; Continue"
            />
          </Margin>
        </SetupStep>
        <SetupStep
          isInitiallyOpen={activeStep === 2}
          stepLabel="Step 2"
          title="Test your API Request"
        >
          <NonFormaticFieldWrapper isTitleOnly={true} title="">
            {isHook ? (
              <HookTester appId={params.id} version={params.version} />
            ) : (
              <RequestTester
                appId={params.id}
                canConnectAuth={isAuthSetupComplete}
                inputFields={initialTrigger.operation?.inputFields}
                needsAuthMessage={
                  <NeedsAuthMessage
                    integrationId={params.id}
                    version={params.version}
                  />
                }
                objectKey={params.triggerKey}
                requests={this.requestsForTesting}
                testType="trigger"
                versionNumber={params.version}
              />
            )}
          </NonFormaticFieldWrapper>
          {!isHook && (
            <Margin margin="-20px 0px 5px 0px">
              <SaveButtonGroup
                isDisabled={this.isSaveDisabled}
                isSaving={isSaving}
                onSave={this.onIncrementActiveStep}
                saveText="Finish Testing &amp; Continue"
              />
            </Margin>
          )}
        </SetupStep>
        <SetupStep
          isInitiallyOpen={activeStep === 3}
          stepLabel="Step 3"
          title="Define your Output"
        >
          <NonFormaticFieldWrapper
            helpText={`Enter JSON-formatted sample output data for the most commonly used fields from this Trigger to use in Zapier as sample data if users don’t test it. <a href="${BASE_DOC_URL_VB}/build/sample-data#sample-data" target="_blank">Learn more</a>.`}
            title="Sample Data"
          >
            {({ fieldLabel }) => (
              <Fragment>
                {fieldLabel}
                <SampleOutputEditor
                  meta={
                    highlightField.field === highlightTriggerApiKeys.sample && {
                      info: highlightField,
                    }
                  }
                  onChange={this.onUpdateSample}
                  testRequestOutput={testRequest.output}
                  value={sampleText}
                />
              </Fragment>
            )}
          </NonFormaticFieldWrapper>
          <Margin margin="0px 0px 30px 0px">
            <LegacyButton
              color="secondary"
              disabled={!trigger.operation?.sample ?? isReadOnly}
              onClick={this.onGenerateOutputClick}
              type="button"
            >
              Generate Output Field Definitions
            </LegacyButton>
          </Margin>
          <NonFormaticFieldWrapper
            helpText={`Add user friendly names for the most popular fields from API output data. <a href="${BASE_DOC_URL_VB}/build/sample-data#output-fields" target="_blank">Learn more</a>.`}
            title="Output Fields"
          >
            <OutputFieldsEditor
              initialItems={trigger.operation?.outputFields ?? []}
              isDisabled={isReadOnly}
              key={outputFieldsEditorKey}
              meta={
                highlightField.field ===
                  highlightTriggerApiKeys.outputFields && {
                  info: highlightField,
                }
              }
              onUpdate={this.onUpdateOutputFields}
            />
          </NonFormaticFieldWrapper>
          <Margin margin="-20px 0px 5px 0px">
            <SaveButtonGroup
              isDisabled={this.isSaveDisabled}
              isSaving={isSaving}
              onSave={this.onSaveAndIncrementActiveStep}
              saveText="Save Output &amp; Finish"
            />
          </Margin>
        </SetupStep>
      </EditorBodyLayout>
    );
  }
}

export default TriggerApi;
